import { Banner, Button, Empty, Input, Loader, Surface, Table, Text } from '@cloudflare/kumo'
import { ArrowsClockwise, CurrencyKrw, MagnifyingGlass, Trash } from '@phosphor-icons/react'
import type { PriceCacheEntry } from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { HelpPanel } from '../components/help-panel'
import { PageHeader } from '../components/page-header'
import { useGetToken } from '../lib/auth'
import { fmtInt, toMessage } from '../lib/format'
import { TablePagination } from '../lib/table-pagination'
import { usePagedList } from '../lib/use-page'

const won = (n: number | null) => (n === null ? '—' : `₩${fmtInt(Math.round(n))}`)

/**
 * 재료 단가 캐시 (KV) — 생성 때 찾은 한국 시세(소매·도매·평균). 24시간 뒤 자동으로 사라지고 다시 검색된다.
 * 잘못 찾힌 값은 여기서 지우면 다음 생성에서 새로 검색한다.
 */
export function PricesPage() {
  const getToken = useGetToken()
  const [q, setQ] = useState('')
  const [items, setItems] = useState<PriceCacheEntry[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState<PriceCacheEntry | null>(null)
  const [seedOpen, setSeedOpen] = useState(false)
  const paged = usePagedList(items)

  const reload = useCallback(
    async (query: string) => {
      setBusy(true)
      try {
        const r = await adminApi.prices.list(getToken, query.trim())
        setItems(r.items)
        setTotal(r.total)
        setError(null)
      } catch (err) {
        setError(toMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [getToken],
  )

  useEffect(() => {
    void reload('')
  }, [reload])

  const remove = async () => {
    if (!deleting) return
    setBusy(true)
    try {
      await adminApi.prices.remove(getToken, deleting.name)
      setItems((prev) => prev?.filter((p) => p.name !== deleting.name) ?? prev)
      setNotice(`"${deleting.name}" 단가를 지웠어요. 다음 생성에서 다시 검색해요.`)
      setDeleting(null)
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const seed = async () => {
    setBusy(true)
    try {
      await adminApi.prices.seed(getToken)
      setSeedOpen(false)
      setNotice('단가 미리 채우기를 시작했어요. 몇 분 뒤 새로고침해 보세요.')
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="재료 단가"
        description="생성할 때 웹 검색으로 찾아 둔 재료 시세예요. 제조가 한국 공장이라 전부 한국 시세(원/kg·L·개)예요."
        actions={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setSeedOpen(true)}>
              자주 쓰는 재료 미리 채우기
            </Button>
            <Button
              variant="secondary"
              icon={<ArrowsClockwise />}
              loading={busy}
              onClick={() => void reload(q)}
            >
              새로고침
            </Button>
          </>
        }
      />

      <HelpPanel
        id="prices"
        steps={[
          <>재료 이름으로 검색하세요 (예: 참깨, 밀가루).</>,
          <>
            결과의 원가가 이상해 보이면 여기서 그 재료의 단가를 확인하세요. 값이 틀렸으면{' '}
            <b>삭제</b>하세요 — 다음 생성에서 다시 검색해요.
          </>,
        ]}
        notes={[
          '단가는 24시간 동안만 기억해요. 시세가 바뀌고, 잘못 찾힌 값이 계속 남지 않게 하려는 거예요.',
          '여기 없는 재료는 생성 때 웹 검색으로 찾고, 그것도 실패하면 AI 가 추정해요.',
          '평균 = 소매가와 도매가의 평균 (하나만 있으면 그 값). 원가 계산에는 평균을 써요.',
        ]}
      />

      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}
      {notice && <Banner variant="default" title={notice} />}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Input
            label="재료 검색"
            placeholder="재료 이름"
            value={q}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && void reload(q)
            }
          />
        </div>
        <Button
          variant="primary"
          icon={<MagnifyingGlass />}
          loading={busy}
          onClick={() => void reload(q)}
        >
          검색
        </Button>
      </div>

      <Surface className="overflow-x-auto rounded-lg p-0">
        {items === null ? (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        ) : items.length === 0 ? (
          <Empty
            icon={<CurrencyKrw size={40} className="text-kumo-inactive" />}
            title={total === 0 ? '기억해 둔 단가가 없어요' : '검색 결과가 없어요'}
            description={
              total === 0
                ? '제품을 생성하면 그때 찾은 재료 단가가 24시간 동안 여기에 쌓여요.'
                : '다른 이름으로 검색해 보세요.'
            }
          />
        ) : (
          <Table>
            <Table.Header variant="compact">
              <Table.Row>
                <Table.Head>재료</Table.Head>
                <Table.Head>단위</Table.Head>
                <Table.Head>소매가</Table.Head>
                <Table.Head>도매가</Table.Head>
                <Table.Head>평균 (원가에 사용)</Table.Head>
                <Table.Head>
                  <span className="sr-only">작업</span>
                </Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paged.slice.map((p) => (
                <Table.Row key={p.name}>
                  <Table.Cell className="font-medium">{p.name}</Table.Cell>
                  <Table.Cell>{p.unit}</Table.Cell>
                  <Table.Cell className="tabular-nums">{won(p.retailKrw)}</Table.Cell>
                  <Table.Cell className="tabular-nums">{won(p.wholesaleKrw)}</Table.Cell>
                  <Table.Cell className="font-medium tabular-nums">{won(p.avgKrw)}</Table.Cell>
                  <Table.Cell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash />}
                      disabled={busy}
                      onClick={() => setDeleting(p)}
                    >
                      삭제
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>
      <TablePagination
        page={paged.page}
        setPage={paged.setPage}
        perPage={paged.perPage}
        total={paged.total}
      />
      {total > 0 && (
        <Text variant="secondary" size="xs">
          기억 중인 재료 {fmtInt(total)}개 (한 번에 최대 100개 표시 — 검색으로 좁히세요)
        </Text>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="이 단가를 지울까요?"
        description={`"${deleting?.name}" 을(를) 지우면 다음 생성에서 웹 검색으로 다시 찾아요.`}
        confirmLabel="삭제"
        destructive
        busy={busy}
        onConfirm={() => void remove()}
        onClose={() => setDeleting(null)}
      />
      <ConfirmDialog
        open={seedOpen}
        title="자주 쓰는 재료 단가를 미리 채울까요?"
        description="웹 검색을 여러 번 호출해요(비용 발생). 백그라운드에서 몇 분 걸려요."
        confirmLabel="시작"
        busy={busy}
        onConfirm={() => void seed()}
        onClose={() => setSeedOpen(false)}
      />
    </div>
  )
}
